import type { MasterTimeOffReason } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useState } from "react"

import { useCabinetAccess } from "../../access/use-access"
import { fetchTimeOffPreview, fetchTimeOffRequests, recallTimeOff, requestTimeOff } from "../../api/master-time-off.api"
import { isApiProblem } from "../../api/problem"
import { todayInZone } from "../../lib/format"
import { resolveBranch } from "../../shell/use-branch"
import { Badge, Button, Chip, Chips, SkeletonText } from "../../ui"
import { useResource } from "../directories/use-resource"
import { MasterBackBar, MasterFrame, MasterNoEmployee } from "./master-frame"
import { REASONS, STATE_TEXT, STATE_TONE, daysLine, periodProblem, periodWords, priceText } from "./master-time-off"
import styles from "./master-time-off-screen.module.css"

/**
 * 5-М6 «Отпроситься» — /my/time-off (ТЗ 05 §5.8, вариант А — решение владельца).
 *
 * Мастер просит — администратор оформляет. Просьба уходит делом в ленту тех, кто ведёт
 * графики, с кнопкой «Оформить отсутствие»; оформленное отсутствие закрывает дело само,
 * и здесь запрос становится «администратор закрыл дни». Отменить свои записи самому нельзя:
 * это делает администратор, и каждому клиенту уходит уведомление.
 */
export function MasterTimeOffScreen(): ReactNode {
  const { context } = useCabinetAccess()
  const branch = resolveBranch(context, null)
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone
  const today = todayInZone(timeZone)

  const [reason, setReason] = useState<MasterTimeOffReason>("vacation")
  const [from, setFrom] = useState(today)
  const [to, setTo] = useState(today)
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const local = periodProblem(from, to, today)
  const preview = useResource(
    local !== null || branch === null
      ? null
      : (signal) => fetchTimeOffPreview({ branchId: branch.id, dateFrom: from, dateTo: to }, signal),
    local !== null || branch === null ? null : `timeoff-preview:${branch.id}:${from}:${to}:${tick}`,
  )
  const requests = useResource((signal) => fetchTimeOffRequests(signal), `timeoff-list:${tick}`)

  if (context.employeeId === null) {
    return (
      <MasterFrame active="week">
        <MasterNoEmployee title="Отпроситься" />
      </MasterFrame>
    )
  }

  const send = async (): Promise<void> => {
    if (branch === null || local !== null) return
    setBusy("send")
    setProblem(null)
    try {
      await requestTimeOff({ branchId: branch.id, dateFrom: from, dateTo: to, reason })
      setTick((value) => value + 1)
    } catch (caught) {
      if (!isApiProblem(caught)) throw caught
      setProblem(caught.detail ?? caught.title)
    } finally {
      setBusy(null)
    }
  }

  const recall = async (id: string): Promise<void> => {
    setBusy(id)
    setProblem(null)
    try {
      await recallTimeOff(id)
      setTick((value) => value + 1)
    } catch (caught) {
      if (!isApiProblem(caught)) throw caught
      setProblem(caught.detail ?? caught.title)
    } finally {
      setBusy(null)
    }
  }

  const price = preview.data === null ? null : priceText(preview.data)
  const overlaps = preview.data?.overlapsTimeOff === true
  const items = requests.data?.items ?? []

  return (
    <MasterFrame active="week">
      <MasterBackBar to="/my/week" label="Моя неделя" />
      <div className={styles.body}>
        <div>
          <h1 className={styles.title}>Отпроситься</h1>
          <div className={styles.sub}>Запрос администратору</div>
        </div>

        <div>
          <div className={styles.label}>Причина</div>
          <Chips label="Причина">
            {REASONS.map((item) => (
              <Chip key={item.id} className={styles.reason} active={reason === item.id} onClick={() => setReason(item.id)}>
                {item.label}
              </Chip>
            ))}
          </Chips>
        </div>

        <div className={styles.dates}>
          <label className={styles.date}>
            <span className={styles.label}>С</span>
            <input
              type="date"
              min={today}
              value={from}
              onChange={(event) => {
                setFrom(event.target.value)
                if (to < event.target.value) setTo(event.target.value)
                setProblem(null)
              }}
            />
          </label>
          <label className={styles.date}>
            <span className={styles.label}>По</span>
            <input
              type="date"
              min={from}
              value={to}
              onChange={(event) => {
                setTo(event.target.value)
                setProblem(null)
              }}
            />
          </label>
        </div>

        {local !== null ? (
          <div className={styles.problem}>{local}</div>
        ) : preview.data === null ? (
          <SkeletonText widths={[260, 220]} height={14} />
        ) : (
          <div className={styles.card}>
            <div className={styles.cardTitle}>Цена просьбы — до отправки</div>
            <div>
              {price?.main}{" "}
              {price?.clients === null || price === null ? null : (
                <>
                  <b className={styles.clients}>{price.clients}</b> — их перенесёт или отменит администратор, каждому
                  уйдёт уведомление.
                </>
              )}
            </div>
            {preview.data.days.length === 0 ? null : <div className={styles.sub}>{daysLine(preview.data)}</div>}
          </div>
        )}

        {overlaps ? (
          <div className={styles.problem}>В этот период у вас уже оформлено отсутствие. Выберите другие дни.</div>
        ) : null}

        <div className={styles.variant}>
          <b>Запрос уйдёт администратору</b> в его уведомления, и он сам закроет дни в графике. Когда в вашей неделе
          появится отсутствие — значит, оформлено.
        </div>

        {problem === null ? null : (
          <div className={styles.problem} role="alert">
            {problem}
          </div>
        )}

        {requests.data === null ? null : items.length === 0 ? null : (
          <div className={styles.card}>
            <div className={styles.cardTitle}>Мои запросы</div>
            {items.map((item) => (
              <div key={item.id}>
                <div className={styles.sentHead}>
                  <b>{periodWords(item.dateFrom, item.dateTo)}</b>
                  <Badge tone={STATE_TONE[item.state]}>{STATE_TEXT[item.state]}</Badge>
                </div>
                <div className={styles.note}>{item.body}</div>
                {item.state === "sent" ? (
                  <Button size="sm" variant="ghost" loading={busy === item.id} onClick={() => void recall(item.id)}>
                    Отозвать запрос
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        )}

        <div className={styles.note}>
          Задним числом отсутствие оформляет администратор. Период, пересекающийся с уже оформленным, не отправится.
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          variant="primary"
          className={styles.send}
          loading={busy === "send"}
          deniedReason={local ?? (overlaps ? "В этот период отсутствие уже оформлено" : undefined)}
          onClick={() => void send()}
        >
          Отправить администратору
        </Button>
      </div>
    </MasterFrame>
  )
}
